<script setup>
    // Builder.io
    import { getEnv } from '@/composables/helpers/getEnv'
    import { Content, isPreviewing, isEditing } from '@builder.io/sdk-vue'
    // Internal
    import { getComponents } from '@/composables/builder/blocks'
    import { getBuilderContent } from '@/composables/builder/content'
    import { getSeoOptions } from '@/composables/data/cacSeo'
    import { getModelFromURL } from '@/composables/builder/model'
    import { useGlobalStore } from '@/stores/global'

    const { $config } = useNuxtApp()
    const { urlExceptions } = useGlobalStore() || {}
    const builderEnv = getEnv('builderEnv')

    const route = useRoute()
    const path = builderEnv !== 'architecturecruise.com' ? route.path : ( route.path === '/' ? '/city-tours/first-lady' : route.path )
    const model_options = getModelFromURL(path, 'page', $config, urlExceptions)
    const model = model_options.model ? model_options.model : 'page'
    const options = model_options.options

    const can_show_content = ref(isPreviewing(route.fullPath))
    const builder_content = ref(isPreviewing(route.fullPath))

    const { content, api_key, error } = await getBuilderContent({ url: path })

    if (error.statusCode === 500) { throw error }

    if (!isPreviewing(route.fullPath)) {
        if (error) { throw error }
        if (content.value?.data && model !== 'nuxt') {
            builder_content.value = content.value
            const seoOptions = getSeoOptions(content.value, model)
            if ( seoOptions && seoOptions.title ) {
                if (content.value.data?.seo?.seo?.removeTitleTail) {
                    useHead({
                        titleTemplate: '%s'
                    })
                }
                useSeoMeta({
                    title: seoOptions.title,
                    ogTitle: seoOptions.title,
                    description: seoOptions.description,
                    ogDescription: seoOptions.description,
                    ogImage: seoOptions.webpage?.image || '/social.jpg',
                    twitterCard: 'summary_large_image',
                })
            }
        } else {
            builder_content.value = false
        }
        can_show_content.value = content ? true : isPreviewing(route.fullPath)
        builder_content.value = content ? content.value : isPreviewing(route.fullPath)
    }

    if (model === 'nuxt' && options.component === 'OpenHouseChicago') {
        const clean_url = route.path.replace('/open-house-chicago', '')
        if (clean_url.length > 1) {
            const { data } = await useAsyncData('ohc-seo', () => $fetch(`https://app.openhousechicago.org/api/seo?url=/${clean_url}`))
            if (data.value?.response) {
                const ohcSEO = data.value?.response
                useSeoMeta({
                    title: ohcSEO.title + ' | Open House Chicago',
                    ogTitle: ohcSEO.title + ' | Open House Chicago',
                    description: ohcSEO.description,
                    ogDescription: ohcSEO.description,
                    ogImage: ohcSEO.image || '/social.jpg',
                    twitterCard: 'summary_large_image',
                })
            }
        }
    }

    const editorUpdate = (event) => {
        const { data } = event.data
        const new_content = { ...content.value }
        new_content.data = { ...new_content.data, ...data.data.data }
        content.value = new_content
        can_show_content.value = true
    }

    const builderMessage = (event) => {
        if (event.data?.type !== 'builder.contentUpdate') return
        editorUpdate(event)
    }

    onMounted(() => {
        if (isPreviewing(route.fullPath)) {
            window.addEventListener('message', builderMessage, '*')
        }
    })

    onBeforeUnmount(() => {
        if (isPreviewing(route.fullPath)) {
            window.removeEventListener('message', builderMessage)
        }
    })

</script>

<template>
    <ProjectPage v-if="model && can_show_content">
        <GlobalBreadcrumbs />
        <template v-if="can_show_content && content">
            <TeamMemberDetailHeader v-if="model ==='team-members'" :member="content.data" />
            <CityToursHeader v-if="model === 'tours'" :content="content" />
            <CityToursDetails v-if="model === 'tours'" :tour="content" />
            <ProgramDetailHeader v-if="model === 'programs-events'" :page="content" />
            <EncyclopediaArticleDetailHeader v-if="model === 'architecture-encyclopedia-articles'" :article="content" />
            <ExhibitionHero v-if="model === 'exhibitions'" :exhibition="content" />
            <BuildingsHeader v-if="model === 'buildings'" :page="content" />
            <BuildingsDetail v-if="model === 'buildings'" :building="content.data" />
        </template>
        <main id="main" key="page" class="mess-page page.vue" tabindex="-1">
            <Content
                :api-key="api_key"
                :model="model"
                :content="builder_content"
                :customComponents="getComponents(api_key)" />
        </main>
    </ProjectPage>
    <template v-else>
        <component :is="options.component" v-if="options.component" />
    </template>
</template>
